import { useEffect, useMemo, useState } from 'react';
import usePPVInfo from './usePPVInfo';
import moment from 'moment';
import { IStore } from '../interfaces/StoreTypes';
import { useSelector } from 'react-redux';
import { useConfigurationSections } from './useConfiguration';

interface PPvHookReturn {
    isShowBuyNowCta: boolean;
    isShowReminderCta: boolean;
    isAvailableSoonCta: boolean;
    isStreamingNow: boolean;
    isSubscribed: boolean;
    isRewind: boolean;
}

const useShowPPvCta = (selectedPPVEvent, ppvSubscribedArr = []): PPvHookReturn => {
    // Using React Query instead of Redux for configuration
    const { ppvConfigV2 } = useConfigurationSections();
    const { events = [] } = ppvConfigV2 || {};
    const eventEndTime = useMemo(() => {
        if (!selectedPPVEvent) return null;
        return events.find((el) => el.eventId === selectedPPVEvent.eventId)?.eventEndTimeTs ?? null;
    }, [events, selectedPPVEvent]);

    const { ppvSubscribedList } = usePPVInfo();
    const currentTime = moment();
    const purchaseWindowStart = moment(selectedPPVEvent?.purchaseWindowStart);
    const purchaseWindowEnd = moment(selectedPPVEvent?.purchaseWindowEnd).endOf('day');
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [isShowBuyNowCta, setShowBuyNowCta] = useState(false);
    const [isShowReminderCta, setShowReminderCta] = useState(false);
    const [isAvailableSoonCta, setAvailableSoonCta] = useState(false);
    const [isStreamingNow, setStreamingNow] = useState(false);
    const [isRewind, setRewind] = useState(false);

    useEffect(() => {
        const subscribedList = ppvSubscribedArr.length ? ppvSubscribedArr : ppvSubscribedList;
        const isSubscribed =
            subscribedList.length > 0 && subscribedList.some((sub) => sub?.eventId === selectedPPVEvent?.eventId);
        const isCurrentTimeInPurchaseWindow = currentTime.isBetween(
            purchaseWindowStart,
            purchaseWindowEnd,
            undefined,
            '[]'
        );
        const isRewindedEvent = eventEndTime && currentTime.isAfter(eventEndTime);

        // For rewind events, check if we're still within the rewind availability period
        const isWithinRewindPeriod = isRewindedEvent && selectedPPVEvent?.rewindDays > 0;

        // Show Buy Now button if:
        // 1. Not subscribed AND within purchase window, OR
        // 2. Not subscribed AND event is in rewind mode with rewind days available
        const isShowBuyNowCta = !isSubscribed && (isCurrentTimeInPurchaseWindow || isWithinRewindPeriod);

        const isShowReminderCta =
            (isSubscribed && currentTime.isBefore(selectedPPVEvent?.eventBroadcastTs)) || isCurrentTimeInPurchaseWindow;
        const isAvailableSoonCta = isSubscribed && currentTime.isBefore(selectedPPVEvent?.eventBroadcastTs);
        const isStreamingNow =
            isSubscribed &&
            currentTime.isAfter(selectedPPVEvent?.eventBroadcastTs) &&
            currentTime.isBefore(eventEndTime);

        setIsSubscribed(isSubscribed);
        setShowBuyNowCta(isShowBuyNowCta);
        setShowReminderCta(isShowReminderCta);
        setAvailableSoonCta(isAvailableSoonCta);
        setStreamingNow(isStreamingNow);
        setRewind(isRewindedEvent);
    }, [selectedPPVEvent, ppvSubscribedList, eventEndTime]);

    return {
        isShowBuyNowCta,
        isShowReminderCta,
        isAvailableSoonCta,
        isStreamingNow,
        isSubscribed,
        isRewind,
    };
};

export default useShowPPvCta;
